import { useRef, useCallback, useEffect } from 'react';
import umlViewportEngine from '../../Core/UmlViewportEngine.js';
import CanvasGeometry from './CanvasGeometry.js';

/**
 * Viewport pan/zoom interactions for the UML canvas: background drag-to-pan,
 * Ctrl+wheel zoom-to-cursor, and screen -> world coordinate conversion.
 * @param {React.RefObject} canvasRef - ref to the canvas root element
 * @param {Function} clearSelection - clears the current shape selection
 * @returns {{toWorldPoint: Function, handleBackgroundPointerDown: Function, handleBackgroundPointerMove: Function, handleBackgroundPointerUp: Function}}
 */
export default function useViewportInteractions(canvasRef, clearSelection) {
    const isPanning = useRef(false);
    const lastPointerPos = useRef({ x: 0, y: 0 });

    const toWorldPoint = useCallback(
        function toWorldPoint(clientX, clientY) {
            const rect = canvasRef.current.getBoundingClientRect();
            return {
                x: (clientX - rect.left - umlViewportEngine.panX) / umlViewportEngine.scale,
                y: (clientY - rect.top - umlViewportEngine.panY) / umlViewportEngine.scale,
            };
        },
        [canvasRef],
    );

    const handleBackgroundPointerDown = useCallback(
        function startPan(event) {
            if (!CanvasGeometry.isBackgroundElement(event.target)) {
                return; // click was on a shape/connector/handle, not the background
            }
            clearSelection();
            isPanning.current = true;
            lastPointerPos.current = { x: event.clientX, y: event.clientY };
            document.body.style.cursor = 'grabbing';
        },
        [clearSelection],
    );

    const handleBackgroundPointerMove = useCallback(function continuePan(event) {
        if (!isPanning.current) {
            return;
        }
        const dx = event.clientX - lastPointerPos.current.x;
        const dy = event.clientY - lastPointerPos.current.y;
        lastPointerPos.current = { x: event.clientX, y: event.clientY };
        umlViewportEngine.pan(dx, dy);
    }, []);

    const handleBackgroundPointerUp = useCallback(function stopPan() {
        isPanning.current = false;
        document.body.style.cursor = '';
    }, []);

    // Native wheel listener: React's synthetic onWheel is passive, so
    // preventDefault() there is a no-op — a real listener with { passive: false } is required.
    useEffect(
        function attachNativeWheelListener() {
            const el = canvasRef.current;
            if (!el) {
                return undefined;
            }

            function onWheel(event) {
                if (!event.ctrlKey) {
                    return;
                }
                event.preventDefault();
                const rect = el.getBoundingClientRect();
                const cursorX = event.clientX - rect.left;
                const cursorY = event.clientY - rect.top;
                umlViewportEngine.zoomAt(cursorX, cursorY, event.deltaY > 0 ? -0.1 : 0.1);
            }

            el.addEventListener('wheel', onWheel, { passive: false });
            return function detachNativeWheelListener() {
                el.removeEventListener('wheel', onWheel);
            };
        },
        [canvasRef],
    );

    return { toWorldPoint, handleBackgroundPointerDown, handleBackgroundPointerMove, handleBackgroundPointerUp };
}
